import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Users, Search, Briefcase, MapPin, ArrowRight, CheckCircle,
  Clock, MessageSquare, ChevronDown, ChevronUp, Star, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../contexts/CountryContext';
import LocalProsPanel from './LocalProsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TYPE_LABELS = {
  mortgage_advisor: 'Mortgage Advisor',
  solicitor: 'Solicitor / Lawyer',
  surveyor: 'Surveyor / Inspector',
  estate_agent: 'Estate Agent',
  financial_advisor: 'Financial Advisor',
};

const FindProfessionals = ({ userRole = 'buyer' }) => {
  const { country } = useCountry();
  const [professionals, setProfessionals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null);
  const [selectedPro, setSelectedPro] = useState(null);
  const [consultMessage, setConsultMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [myConsultations, setMyConsultations] = useState([]);

  useEffect(() => {
    fetchDirectory();
    fetchMyConsultations();
  }, []);

  const fetchDirectory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/professionals/directory`);
      setProfessionals(res.data.professionals || []);
      setCategories(res.data.categories || []);
    } catch { /* handled */ }
    setLoading(false);
  };

  const fetchMyConsultations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/consultations/my-requests`);
      setMyConsultations(res.data || []);
    } catch { /* not critical */ }
  };

  const handleRequestConsultation = async (pro) => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/consultations/request`, {
        professional_id: pro.id,
        service_type: pro.professional_type || 'general',
        message: consultMessage,
        country,
      });
      toast.success(`Consultation request sent to ${pro.name}!`);
      setSelectedPro(null);
      setConsultMessage('');
      fetchMyConsultations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  const hasExistingConsultation = (proId) => {
    return myConsultations.some(c => c.professional_id === proId && ['pending', 'accepted'].includes(c.status));
  };

  const filtered = activeType ? professionals.filter(p => p.professional_type === activeType) : professionals;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="find-professionals">
      {/* Primary source: 561 real Google-rated pros from /api/pro-leads.
          This is the panel that buyers actually need — solicitors, mortgage
          brokers, estate agents, property managers across 23 cities. */}
      <LocalProsPanel role={userRole} country={country} dataTestId="find-pros-leads" />

      {/* Header */}
      <div className="mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-2">Or request a free consultation</h2>
        <p className="text-sm text-slate-500">
          {userRole === 'seller' ? 'Choose a verified Shopperden professional for a free consultation.' :
           userRole === 'tenant' ? 'Letting agents and solicitors who give free first calls.' :
           'Self-registered Shopperden professionals offering free consultations — no unlock fee.'}
        </p>
      </div>

      {/* Category Filters — only show when we actually have self-registered pros */}
      {professionals.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveType(null)}
          className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${!activeType ? 'bg-[#E07A3A] text-white' : 'border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A]'}`}
          data-testid="filter-all-pros"
        >
          All ({professionals.length})
        </button>
        {categories.map((cat) => (
          <button
            key={cat.type}
            onClick={() => setActiveType(cat.type)}
            className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${activeType === cat.type ? 'bg-[#E07A3A] text-white' : 'border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A]'}`}
            data-testid={`filter-${cat.type}`}
          >
            {cat.label} ({cat.count})
          </button>
        ))}
      </div>
      )}

      {/* Professional Cards */}
      {filtered.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((pro) => {
            const existing = hasExistingConsultation(pro.id);
            return (
              <div key={pro.id} className="luxury-card p-5 flex flex-col" data-testid={`pro-card-${pro.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-xs">{(pro.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[#0F172A] font-semibold text-sm truncate">{pro.name}</h4>
                    <p className="text-[10px] text-[#E07A3A] font-semibold uppercase tracking-wider">
                      {TYPE_LABELS[pro.professional_type] || (pro.professional_type || 'general').replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>

                {pro.location && (
                  <p className="text-xs text-slate-400 mb-2 flex items-center gap-1"><MapPin className="h-3 w-3" />{pro.location}</p>
                )}

                {pro.bio && (
                  <p className="text-xs text-slate-500 leading-relaxed mb-2 line-clamp-2">{pro.bio}</p>
                )}

                {pro.service_areas && pro.service_areas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {pro.service_areas.map(area => (
                      <span key={area} className="px-2 py-0.5 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-medium border border-slate-100">{area}</span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-3 mb-3 text-xs text-slate-400">
                  {pro.years_experience && <span>{pro.years_experience}+ yrs</span>}
                  {pro.qualifications && <span className="truncate">{pro.qualifications}</span>}
                  <span><Briefcase className="h-3 w-3 inline mr-0.5" />{pro.completed_deals || 0} deals</span>
                </div>

                <div className="mt-auto">
                  {existing ? (
                    <div className="text-center py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold uppercase tracking-wider" data-testid={`already-requested-${pro.id}`}>
                      <CheckCircle className="h-3.5 w-3.5 inline mr-1" />Consultation Requested
                    </div>
                  ) : selectedPro?.id === pro.id ? (
                    <div className="space-y-2" data-testid={`consult-form-${pro.id}`}>
                      <textarea
                        value={consultMessage}
                        onChange={(e) => setConsultMessage(e.target.value)}
                        placeholder="Introduce yourself and what you need..."
                        className="w-full border border-slate-200 p-2 text-xs focus:border-[#E07A3A] focus:outline-none resize-none h-16"
                        data-testid={`consult-msg-${pro.id}`}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRequestConsultation(pro)}
                          disabled={sending}
                          className="flex-1 bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider py-2"
                          data-testid={`send-consult-${pro.id}`}
                        >
                          {sending ? 'Sending...' : 'Send Request'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setSelectedPro(null); setConsultMessage(''); }} className="text-xs">Cancel</Button>
                      </div>
                      <p className="text-[10px] text-slate-400 text-center">Free consultation request</p>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setSelectedPro(pro)}
                      className="w-full bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-widest uppercase font-semibold py-4"
                      data-testid={`request-btn-${pro.id}`}
                    >
                      Request Free Consultation <ArrowRight className="ml-1.5 h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        professionals.length === 0 ? null : (
        <div className="luxury-card p-12 text-center">
          <Users className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg font-['Playfair_Display']">No matching consultation pros</p>
          <p className="text-slate-400 text-sm mt-2">Try a different category — or use the Google-rated pros panel above for direct contact.</p>
        </div>
        )
      )}

      {/* Join CTA */}
      <div className="mt-8 bg-[#0F172A] p-6 text-center" data-testid="pro-cta-banner">
        <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">Are You a Property Professional?</h3>
        <p className="text-white/50 text-xs mb-4 max-w-md mx-auto">Join Shopperden and receive warm buyer leads — free consultation requests from verified users.</p>
        <a href="/register">
          <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-widest uppercase font-semibold px-6 py-4" data-testid="pro-register-cta">
            Register as Professional <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Button>
        </a>
      </div>
    </div>
  );
};

export default FindProfessionals;
